// src/pages/Inductions.tsx — Site Inductions & Worker Onboarding
// Follows frontend-skill.md conventions exactly:
//   - Functional components + hooks only
//   - Tailwind utility classes only
//   - Primary: indigo-600, Success: green-500, Warning: amber-500, Danger: red-500
//   - Neutrals: slate-100 through slate-900
//   - Page wrapper: <div className="p-6 space-y-6">

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_WORKER_INDUCTIONS, MOCK_INDUCTION_MODULES,
  WorkerInduction, InductionModule, InductionStatus, InductionType,
} from '../utils/mockDataWHS'
import { MOCK_JOBS } from '../utils/mockData'
import { formatDate } from '../utils/formatters'
import { useQuery } from '@tanstack/react-query'
import { api } from '../utils/api'
import {
  ClipboardCheck, Plus, Search, X, CheckCircle, AlertTriangle,
  Clock, Users, QrCode, BookOpen, ChevronRight, ArrowLeft,
  Shield, Award, Phone, Building2, RefreshCw, UserPlus,
  Eye, Zap, GraduationCap, Download,
} from 'lucide-react'
import { MOCK_VOC_RECORDS, VOCRecord, VOC_STATUS_META, VOCStatus, ROLE_TRAINING_MAP, TRAINING_CATEGORIES } from '../utils/mockDataWHSMgmt'

// ─────────────────────────────────────────────────────────────────
// TYPES & CONSTANTS
// ─────────────────────────────────────────────────────────────────

const STATUS_META: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  PENDING:     { label: 'Pending',     bg: 'bg-slate-100',  text: 'text-slate-600',  dot: 'bg-slate-400' },
  IN_PROGRESS: { label: 'In progress', bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500'  },
  PASSED:      { label: 'Passed',      bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500' },
  FAILED:      { label: 'Failed',      bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'   },
  EXPIRED:     { label: 'Expired',     bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500' },
}

const TYPE_META: Record<InductionType, { label: string; bg: string; text: string }> = {
  COMPANY:         { label: 'Company',         bg: 'bg-indigo-50', text: 'text-indigo-700' },
  PROJECT:         { label: 'Project-specific',bg: 'bg-sky-50',    text: 'text-sky-700'   },
  CLIENT_SPECIFIC: { label: 'Client-specific', bg: 'bg-purple-50', text: 'text-purple-700'},
}

const WORKER_TYPE_META = {
  EMPLOYEE:      { label: 'Employee',      bg: 'bg-green-50',  text: 'text-green-700'  },
  SUBCONTRACTOR: { label: 'Subcontractor', bg: 'bg-amber-50',  text: 'text-amber-700'  },
  VISITOR:       { label: 'Visitor',       bg: 'bg-slate-100', text: 'text-slate-600'  },
}

// Days before expiry to warn
const EXPIRY_WARN_DAYS = 30

function daysUntil(dateStr?: string) {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
}

function isExpiringSoon(dateStr?: string) {
  const d = daysUntil(dateStr)
  return d !== null && d <= EXPIRY_WARN_DAYS && d >= 0
}

function isExpired(dateStr?: string) {
  if (!dateStr) return false
  return new Date(dateStr) < new Date()
}

// ─────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: InductionStatus }) {
  const m = STATUS_META[status]
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  )
}

function CredPill({ label, value, expiry }: { label: string; value?: string; expiry?: string }) {
  const expired  = isExpired(expiry)
  const expiring = !expired && isExpiringSoon(expiry)
  const hasValue = value || expiry
  if (!hasValue) return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-xs text-slate-300 italic">Not provided</span>
    </div>
  )
  return (
    <div className={`flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm rounded px-1
      ${expired ? 'bg-red-50' : expiring ? 'bg-amber-50' : ''}`}>
      <span className="text-slate-600">{label}</span>
      <div className="flex items-center gap-2 text-right">
        {value && <span className="text-xs font-mono text-slate-500">{value}</span>}
        {expiry && (
          <span className={`text-xs font-medium ${expired ? 'text-red-700' : expiring ? 'text-amber-700' : 'text-green-700'}`}>
            {expired ? '⚠ Expired ' : expiring ? `⚠ Expires ` : 'Exp. '}{formatDate(expiry)}
          </span>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// WORKER DETAIL MODAL
// ─────────────────────────────────────────────────────────────────

function WorkerDetailModal({
  worker, allWorkers, onClose, onReinduct,
}: {
  worker: WorkerInduction
  allWorkers: WorkerInduction[]
  onClose: () => void
  onReinduct: (w: WorkerInduction) => void
}) {
  // All induction records for this worker across modules
  const workerHistory = allWorkers.filter(w =>
    w.workerId === worker.workerId || w.workerName === worker.workerName
  )
  const canReinduct = worker.status === 'FAILED' || worker.status === 'EXPIRED'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
              {worker.workerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">{worker.workerName}</h2>
              <p className="text-xs text-slate-500">{worker.company ?? 'MCB Civil Electrical'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* Worker type + Site ID */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${WORKER_TYPE_META[worker.workerType].bg} ${WORKER_TYPE_META[worker.workerType].text}`}>
              {WORKER_TYPE_META[worker.workerType].label}
            </span>
            {worker.status === 'INDUCTED' && (
              <button onClick={() => generateInductionCertificate({ ...worker, siteId: worker.uniqueWorkerId, name: (worker as any).employeeName ?? (worker as any).name ?? "" })}
                className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors">
                Print certificate
              </button>
            )}
          {worker.uniqueWorkerId && (
              <span className="flex items-center gap-1 text-xs font-mono font-bold text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200">
                <Award className="w-3 h-3" /> Site ID: {worker.uniqueWorkerId}
              </span>
            )}
            <StatusBadge status={worker.status} />
          </div>

          {/* Contact */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Phone className="w-3.5 h-3.5 text-slate-400" />
            {worker.workerMobile}
          </div>

          {/* Credentials */}
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Credentials on file
            </p>
            <CredPill
              label="White Card"
              value={worker.whiteCardNumber}
              expiry={worker.whiteCardExpiry}
            />
            <CredPill
              label="Electrical Licence"
              value={worker.electricalLicenceNumber}
              expiry={worker.electricalLicenceExpiry}
            />
          </div>

          {/* Induction history */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Induction history
            </p>
            <div className="space-y-2">
              {workerHistory.map(h => (
                <div key={h.id} className="flex items-start justify-between gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{h.inductionTitle}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      <span className={`px-2 py-0.5 rounded-full ${TYPE_META[h.inductionType].bg} ${TYPE_META[h.inductionType].text}`}>
                        {TYPE_META[h.inductionType].label}
                      </span>
                      {h.completedAt && <span>{formatDate(h.completedAt)}</span>}
                      {h.score !== undefined && <span>{h.score}%</span>}
                      {h.expiryDate && (
                        <span className={isExpiringSoon(h.expiryDate) ? 'text-amber-600' : isExpired(h.expiryDate) ? 'text-red-600' : ''}>
                          Exp {formatDate(h.expiryDate)}
                        </span>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={h.status} />
                </div>
              ))}
            </div>
          </div>

          {/* Induction expiry notice */}
          {worker.expiryDate && isExpiringSoon(worker.expiryDate) && !isExpired(worker.expiryDate) && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2.5 border border-amber-200">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              Induction expires in {daysUntil(worker.expiryDate)} days — schedule renewal
            </div>
          )}
          {worker.expiryDate && isExpired(worker.expiryDate) && (
            <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2.5 border border-red-200">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              Induction expired — worker must re-induct before site access
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-slate-50 flex gap-2">
          {canReinduct && (
            <button
              onClick={() => { onReinduct(worker); onClose() }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Re-induct worker
            </button>
          )}
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-white transition-colors ml-auto">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// INDUCTION QUIZ SIMULATOR
// Simulates what the worker sees on their phone / kiosk
// ─────────────────────────────────────────────────────────────────

function QuizSimulatorModal({ module: mod, onClose }: { module: InductionModule; onClose: () => void }) {
  const [step, setStep]     = useState<'register' | 'quiz' | 'result'>('register')
  const [name, setName]     = useState('')
  const [mobile, setMobile] = useState('')
  const [current, setCurrent] = useState(0)
  const [selected, setSelected] = useState<(number | null)[]>(mod.questions.map(() => null))
  const [showAnswer, setShowAnswer] = useState(false)

  const q          = mod.questions[current]
  const answered   = selected[current] !== null
  const isCorrect  = selected[current] === q?.correctIndex
  const totalQ     = mod.questions.length
  const score      = Math.round(
    (selected.filter((s, i) => s === mod.questions[i]?.correctIndex).length / totalQ) * 100
  )
  const passed     = score >= mod.passMark

  const handleSelect = (idx: number) => {
    if (showAnswer) return
    setSelected(prev => { const a = [...prev]; a[current] = idx; return a })
    setShowAnswer(true)
  }

  const handleNext = () => {
    setShowAnswer(false)
    if (current < totalQ - 1) setCurrent(c => c + 1)
    else setStep('result')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

        {/* Phone-like header */}
        <div className="bg-gradient-to-r from-[#1F3864] to-[#2E75B6] px-5 py-4">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-white" />
              <span className="text-white font-semibold text-sm">TrafficProcure</span>
            </div>
            <button onClick={onClose}><X className="w-4 h-4 text-white/70 hover:text-white" /></button>
          </div>
          <p className="text-blue-200 text-xs">Worker induction — {mod.title}</p>
        </div>

        {/* Step: Register */}
        {step === 'register' && (
          <div className="p-5 space-y-4">
            <div className="text-center py-2">
              <ClipboardCheck className="w-10 h-10 text-indigo-600 mx-auto mb-2" />
              <p className="font-semibold text-slate-900">Before you begin</p>
              <p className="text-xs text-slate-500 mt-1">Enter your details to start the induction</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Full name *</label>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. John Smith" className="input w-full text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Mobile number *</label>
              <input value={mobile} onChange={e => setMobile(e.target.value)}
                placeholder="04XX XXX XXX" className="input w-full text-sm" type="tel" />
            </div>
            <div className="bg-amber-50 rounded-lg px-3 py-2.5 text-xs text-amber-700">
              <strong>You'll need:</strong> your White Card number and any relevant trade licences to complete registration.
            </div>
            <button
              onClick={() => setStep('quiz')}
              disabled={!name.trim() || !mobile.trim()}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 transition-colors"
            >
              Start induction →
            </button>
          </div>
        )}

        {/* Step: Quiz */}
        {step === 'quiz' && q && (
          <div className="p-5 space-y-4">
            {/* Progress bar */}
            <div>
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
                <span>Question {current + 1} of {totalQ}</span>
                <span>{Math.round(((current) / totalQ) * 100)}% complete</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                  style={{ width: `${((current) / totalQ) * 100}%` }}
                />
              </div>
            </div>

            <p className="text-sm font-semibold text-slate-900 leading-snug">{q.text}</p>

            <div className="space-y-2">
              {q.options.map((opt, idx) => {
                const isSelected = selected[current] === idx
                const correct    = idx === q.correctIndex
                let cls = 'border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50'
                if (showAnswer && isSelected && correct)  cls = 'border-green-500 bg-green-50 text-green-800'
                if (showAnswer && isSelected && !correct) cls = 'border-red-400 bg-red-50 text-red-800'
                if (showAnswer && !isSelected && correct) cls = 'border-green-400 bg-green-50 text-green-700'
                return (
                  <button
                    key={idx}
                    onClick={() => handleSelect(idx)}
                    className={`w-full text-left text-sm px-4 py-3 rounded-xl transition-colors ${cls}`}
                  >
                    <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span>
                    {opt}
                    {showAnswer && correct && <span className="float-right text-green-600">✓</span>}
                    {showAnswer && isSelected && !correct && <span className="float-right text-red-500">✗</span>}
                  </button>
                )
              })}
            </div>

            {showAnswer && (
              <button
                onClick={handleNext}
                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {current < totalQ - 1 ? 'Next question →' : 'See results'}
              </button>
            )}
          </div>
        )}

        {/* Step: Result */}
        {step === 'result' && (
          <div className="p-5 text-center space-y-4">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${passed ? 'bg-green-100' : 'bg-red-100'}`}>
              {passed
                ? <CheckCircle className="w-10 h-10 text-green-600" />
                : <X className="w-10 h-10 text-red-500" />
              }
            </div>

            <div>
              <p className={`text-2xl font-bold ${passed ? 'text-green-700' : 'text-red-600'}`}>{score}%</p>
              <p className="text-sm text-slate-500 mt-0.5">Pass mark: {mod.passMark}%</p>
              <p className={`font-semibold mt-2 ${passed ? 'text-green-700' : 'text-red-700'}`}>
                {passed ? '✅ Induction passed!' : '❌ Induction failed'}
              </p>
            </div>

            {passed && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3">
                <p className="text-xs text-indigo-600 font-medium mb-1">Your site entry ID</p>
                <p className="text-3xl font-bold font-mono text-indigo-700 tracking-widest">
                  {String(Math.floor(100000 + Math.random() * 900000))}
                </p>
                <p className="text-xs text-indigo-500 mt-1">You'll receive this by SMS. Show it when checking in.</p>
              </div>
            )}

            {!passed && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700">
                You need {mod.passMark}% to pass. Please re-read the induction material and try again.
              </div>
            )}

            <button onClick={onClose} className="w-full py-2.5 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50">
              Close preview
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// REGISTER WORKER MODAL (face-to-face / WHSO-assisted)
// ─────────────────────────────────────────────────────────────────

function RegisterWorkerModal({
  onClose, onRegister,
}: {
  onClose: () => void
  onRegister: (w: Partial<WorkerInduction>) => void
}) {
  const [form, setForm] = useState({
    workerName: '', workerMobile: '', workerType: 'SUBCONTRACTOR' as const,
    company: '', whiteCardNumber: '', whiteCardExpiry: '',
    electricalLicenceNumber: '', electricalLicenceExpiry: '',
    inductionModuleId: MOCK_INDUCTION_MODULES[0]?.id ?? '',
    inductedFaceToFace: true,
  })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const selectedModule = MOCK_INDUCTION_MODULES.find(m => m.id === form.inductionModuleId)
    const uid = String(Math.floor(100000 + Math.random() * 900000))
    onRegister({
      id: `wi_${Date.now()}`,
      inductionModuleId: form.inductionModuleId,
      inductionTitle: selectedModule?.title ?? '',
      inductionType: selectedModule?.type ?? 'COMPANY',
      workerId: `ext_${Date.now()}`,
      workerName: form.workerName,
      workerMobile: form.workerMobile,
      workerType: form.workerType,
      company: form.company,
      whiteCardNumber: form.whiteCardNumber || undefined,
      whiteCardExpiry: form.whiteCardExpiry || undefined,
      electricalLicenceNumber: form.electricalLicenceNumber || undefined,
      electricalLicenceExpiry: form.electricalLicenceExpiry || undefined,
      status: 'PASSED',
      uniqueWorkerId: uid,
      score: 100,
      passed: true,
      completedAt: new Date().toISOString(),
      expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
      inductedByName: 'Sarah Kim (WHSO)',
      notes: 'Face-to-face induction — conducted by WHSO on site',
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-semibold text-slate-900">Register worker — face-to-face</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Full name *</label>
              <input value={form.workerName} onChange={e => set('workerName', e.target.value)}
                placeholder="e.g. Brad Thomson" className="input w-full text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Mobile *</label>
              <input value={form.workerMobile} onChange={e => set('workerMobile', e.target.value)}
                placeholder="04XX XXX XXX" className="input w-full text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Worker type *</label>
              <select value={form.workerType} onChange={e => set('workerType', e.target.value)} className="input w-full text-sm">
                <option value="EMPLOYEE">Employee</option>
                <option value="SUBCONTRACTOR">Subcontractor</option>
                <option value="VISITOR">Visitor</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
              <input value={form.company} onChange={e => set('company', e.target.value)}
                placeholder="e.g. Thomson Electrical Pty Ltd" className="input w-full text-sm" />
            </div>
          </div>

          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Credentials</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">White Card number</label>
                <input value={form.whiteCardNumber} onChange={e => set('whiteCardNumber', e.target.value)}
                  placeholder="WC-QLD-XXXXXXX" className="input w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">White Card expiry</label>
                <input type="date" value={form.whiteCardExpiry} onChange={e => set('whiteCardExpiry', e.target.value)} className="input w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Electrical licence no.</label>
                <input value={form.electricalLicenceNumber} onChange={e => set('electricalLicenceNumber', e.target.value)}
                  placeholder="e.g. 88123QLD" className="input w-full text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Electrical licence expiry</label>
                <input type="date" value={form.electricalLicenceExpiry} onChange={e => set('electricalLicenceExpiry', e.target.value)} className="input w-full text-sm" />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">Induction module *</label>
            <select value={form.inductionModuleId} onChange={e => set('inductionModuleId', e.target.value)} className="input w-full text-sm">
              {MOCK_INDUCTION_MODULES.map(m => (
                <option key={m.id} value={m.id}>
                  {m.title} ({TYPE_META[m.type].label})
                </option>
              ))}
            </select>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-xs text-green-700">
            <strong>Face-to-face induction:</strong> WHSO conducts the induction verbally. Recording here marks the induction as PASSED and issues a unique site entry ID via SMS.
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit"
              className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium">
              Record induction & issue site ID
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MODULE CARD
// ─────────────────────────────────────────────────────────────────

function ModuleCard({
  mod, allInductions, onPreview,
}: {
  mod: InductionModule
  allInductions: WorkerInduction[]
  onPreview: () => void
}) {
  const attempts    = allInductions.filter(w => w.inductionModuleId === mod.id)
  const passed      = attempts.filter(w => w.status === 'PASSED').length
  const passRate    = attempts.length > 0 ? Math.round((passed / attempts.length) * 100) : null
  const typeMeta    = TYPE_META[mod.type]

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeMeta.bg} ${typeMeta.text}`}>
              {typeMeta.label}
            </span>
            <span className="text-xs text-slate-400">v{mod.version}</span>
            <span className="text-xs text-slate-400">Pass mark: {mod.passMark}%</span>
          </div>
          <h3 className="font-semibold text-slate-900 text-sm leading-snug">{mod.title}</h3>
          {mod.jobId && (
            <p className="text-xs text-slate-500 mt-0.5">
              {MOCK_JOBS.find(j => j.id === mod.jobId)?.jobNumber ?? mod.jobId}
            </p>
          )}
          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{mod.description}</p>
        </div>
        <button
          onClick={onPreview}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors flex-shrink-0"
        >
          <Eye className="w-3.5 h-3.5" /> Preview
        </button>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-3 text-center">
        <div>
          <p className="text-lg font-bold text-slate-800">{mod.questions.length}</p>
          <p className="text-xs text-slate-400">Questions</p>
        </div>
        <div>
          <p className="text-lg font-bold text-slate-800">{attempts.length}</p>
          <p className="text-xs text-slate-400">Attempts</p>
        </div>
        <div>
          <p className={`text-lg font-bold ${passRate === null ? 'text-slate-300' : passRate >= 70 ? 'text-green-600' : 'text-red-600'}`}>
            {passRate !== null ? `${passRate}%` : '—'}
          </p>
          <p className="text-xs text-slate-400">Pass rate</p>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// QR CODE PANEL
// ─────────────────────────────────────────────────────────────────

function QRPanel({ modules }: { modules: InductionModule[] }) {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const handleCopy = (url: string, id: string) => {
    navigator.clipboard.writeText(url).catch(() => {})
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700">
        <strong>How it works:</strong> Print or display the QR code at site entry. Workers scan it on their phone, complete registration and the induction quiz, and receive a unique 6-digit site entry ID by SMS — no app download required.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {modules.map(mod => {
          const url = `https://induct.trafficprocure.com.au/${mod.id}`
          const copied = copiedId === mod.id
          return (
            <div key={mod.id} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-2 mb-4">
                <div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${TYPE_META[mod.type].bg} ${TYPE_META[mod.type].text}`}>
                    {TYPE_META[mod.type].label}
                  </span>
                  <p className="font-medium text-slate-900 text-sm mt-1.5 leading-snug">{mod.title}</p>
                  {mod.jobId && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      {MOCK_JOBS.find(j => j.id === mod.jobId)?.jobNumber}
                    </p>
                  )}
                </div>
              </div>

              {/* Placeholder QR code visual */}
              <div className="w-32 h-32 mx-auto bg-slate-900 rounded-xl flex items-center justify-center mb-4 relative overflow-hidden">
                <div className="grid grid-cols-7 gap-0.5 p-3">
                  {Array.from({ length: 49 }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-2.5 h-2.5 rounded-[1px] ${
                        // Fake QR pattern using deterministic pseudo-random based on index + mod.id
                        ((i * 7 + mod.id.charCodeAt(i % mod.id.length)) % 3 === 0)
                          ? 'bg-white' : 'bg-slate-900'
                      }`}
                    />
                  ))}
                </div>
                {/* Corner squares to make it look like a real QR */}
                <div className="absolute top-2 left-2 w-7 h-7 border-4 border-white rounded-sm" />
                <div className="absolute top-2 right-2 w-7 h-7 border-4 border-white rounded-sm" />
                <div className="absolute bottom-2 left-2 w-7 h-7 border-4 border-white rounded-sm" />
              </div>

              {/* URL */}
              <div className="flex items-center gap-2">
                <p className="text-xs font-mono text-slate-500 bg-slate-50 rounded px-2 py-1.5 flex-1 truncate">
                  {url}
                </p>
                <button
                  onClick={() => handleCopy(url, mod.id)}
                  className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors flex-shrink-0 ${
                    copied
                      ? 'bg-green-100 text-green-700'
                      : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                  }`}
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>

              <button className="mt-3 w-full py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                🖨 Print QR code (A5)
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────



// ─────────────────────────────────────────────────────────────────
// PHASE 2 — SITE INDUCTION CERTIFICATE GENERATOR
// Issues a personalised induction certificate to each worker who
// has successfully completed site induction and the quiz.
// Some TMR and Energex contracts require this as proof of induction.
// ─────────────────────────────────────────────────────────────────
function generateInductionCertificate(worker: any, jobContext?: any) {
  const now     = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const expDate = worker.inductionExpiry
    ? new Date(worker.inductionExpiry).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'No expiry — site-specific induction'

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Site Induction Certificate — ${worker.name ?? worker.fullName}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:0;background:#f3f4f6;display:flex;align-items:center;justify-content:center;min-height:100vh;padding:20px}
.cert{background:#fff;width:720px;padding:40px 48px;border:6px solid #1B3A6B;border-radius:8px;box-shadow:0 4px 24px rgba(0,0,0,.15);text-align:center}
h1{font-size:26pt;font-weight:900;color:#1B3A6B;margin:0 0 4px}
.sub{font-size:10pt;color:#888;margin-bottom:20px}
.name{font-size:28pt;font-weight:900;color:#1B3A6B;margin:16px 0;padding:12px 20px;border-top:2px solid #e5e7eb;border-bottom:2px solid #e5e7eb}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin:18px 0;text-align:left}
.info-box{border:1px solid #e5e7eb;border-radius:6px;padding:10px 14px}
.info-lbl{font-size:9pt;color:#888;margin-bottom:3px}
.info-val{font-size:11pt;font-weight:bold;color:#1B3A6B}
.site-id{font-size:36pt;font-weight:900;color:#1B3A6B;font-family:monospace;letter-spacing:8px;margin:14px 0;padding:10px;background:#e8f0fe;border-radius:6px}
.quiz-badge{display:inline-block;background:#dcfce7;color:#166534;border:1.5px solid #86efac;padding:6px 16px;border-radius:20px;font-weight:bold;font-size:10.5pt;margin:10px 0}
.sig-row{display:flex;gap:40px;margin-top:24px;text-align:left}
.sig-block{flex:1}.sig-line{border-bottom:1.5px solid #333;height:30px;margin-bottom:4px}
.sig-lbl{font-size:8pt;color:#888}
.stamp{display:inline-block;border:3px solid #1B3A6B;border-radius:50%;padding:10px 14px;color:#1B3A6B;font-weight:900;font-size:10pt;margin-top:12px;transform:rotate(-8deg)}
.footer{font-size:8pt;color:#aaa;margin-top:18px;border-top:1px solid #eee;padding-top:10px}
@media print{body{background:#fff;min-height:auto}.cert{box-shadow:none;page-break-inside:avoid}}
</style></head><body>
<div class="cert">
  <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px">
    <div style="text-align:left"><h1>MCB Civil Electrical</h1><div class="sub">ABN 12 345 678 901</div></div>
    <div class="stamp">INDUCTED</div>
  </div>

  <div style="font-size:14pt;font-weight:bold;color:#555;text-transform:uppercase;letter-spacing:3px;margin-bottom:4px">Site Induction Certificate</div>
  <div style="font-size:10pt;color:#888">This certifies that the following person has successfully completed MCB Civil Electrical site induction</div>

  <div class="name">${worker.name ?? worker.fullName}</div>

  <div class="info-grid">
    <div class="info-box">
      <div class="info-lbl">Job / site</div>
      <div class="info-val">${worker.jobNumber ?? jobContext?.jobNumber ?? '—'}</div>
    </div>
    <div class="info-box">
      <div class="info-lbl">Induction date</div>
      <div class="info-val">${worker.inductedAt ? new Date(worker.inductedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : now}</div>
    </div>
    <div class="info-box">
      <div class="info-lbl">Company</div>
      <div class="info-val">${worker.company ?? 'MCB Civil Electrical'}</div>
    </div>
    <div class="info-box">
      <div class="info-lbl">Valid until</div>
      <div class="info-val">${expDate}</div>
    </div>
  </div>

  ${worker.siteId ? `<div style="margin:12px 0 4px;font-size:10pt;color:#555;font-weight:bold">SITE ENTRY ID</div><div class="site-id">${worker.siteId}</div>` : ''}

  ${worker.quizScore !== null && worker.quizScore !== undefined ? `<div class="quiz-badge">✓ Induction quiz passed — ${worker.quizScore}/${worker.quizTotal ?? 10} correct</div>` : ''}

  <p style="font-size:10pt;color:#555;margin:14px 0">This worker has been briefed on site safety rules, emergency procedures, personal protective equipment requirements, site access and egress, and MCB Civil Electrical's drug &amp; alcohol and reporting obligations.</p>

  <div class="sig-row">
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">WHSO / Site Manager — name &amp; date</div></div>
    <div class="sig-block"><div class="sig-line"></div><div class="sig-lbl">Worker acknowledgement — name &amp; date</div></div>
  </div>
  <div class="footer">MCB Civil Electrical · Site Induction Certificate · ${worker.siteId ?? ''} · Generated ${now}<br>This certificate remains valid only while the holder complies with all site safety rules and the MCB Civil Electrical Safety Management System.</div>
</div>
</body></html>`

  const win = window.open('', '_blank', 'width=820,height=700')
  if (!win) { alert('Pop-up blocked — allow pop-ups to print certificate'); return }
  win.document.write(html); win.document.close()
  setTimeout(() => { win.focus(); win.print() }, 350)
}
export function Inductions() {
  const { isTier } = useAuthStore()

  // ── State ─────────────────────────────────────────────────────
  const [inductions, setInductions] = useState<WorkerInduction[]>(MOCK_WORKER_INDUCTIONS)

  // Live induction count from Neon (schema differs from local WorkerInduction — full wire in Phase 6 step 4)
  const { data: liveInductionsRes } = useQuery({
    queryKey: ['whs-inductions'],
    queryFn:  async () => {
      const { data } = await api.get<{ count: number }>('/api/whs/inductions')
      return data as any
    },
    staleTime: 60_000,
  })
  const liveInductionCount: number = (liveInductionsRes as any)?.count ?? 0
  const [tab, setTab]               = useState<'workers' | 'modules' | 'qr' | 'training-matrix' | 'voc'>('workers')
  const [vocFilter, setVocFilter]    = useState<VOCStatus | 'ALL'>('ALL')
  const [vocSearch, setVocSearch]    = useState('')
  const [roleFilter, setRoleFilter]  = useState('ALL')
  const [search, setSearch]         = useState('')
  const [statusFilter, setStatusFilter] = useState<InductionStatus | 'ALL'>('ALL')
  const [typeFilter, setTypeFilter]     = useState<InductionType | 'ALL'>('ALL')
  const [jobFilter, setJobFilter]       = useState<string>('ALL')

  const [selectedWorker, setSelectedWorker] = useState<WorkerInduction | null>(null)
  const [previewModule, setPreviewModule]   = useState<InductionModule | null>(null)
  const [showRegister, setShowRegister]     = useState(false)

  const canManage = isTier('B_PC')   // B_PC, B_PM, A_DEVELOPER

  // ── Derived data ──────────────────────────────────────────────
  const stats = useMemo(() => ({
    passed:   inductions.filter(w => w.status === 'PASSED').length,
    pending:  inductions.filter(w => w.status === 'PENDING' || w.status === 'IN_PROGRESS').length,
    failed:   inductions.filter(w => w.status === 'FAILED').length,
    expiring: inductions.filter(w => w.status === 'PASSED' && isExpiringSoon(w.expiryDate)).length,
    expired:  inductions.filter(w => w.status === 'EXPIRED' || isExpired(w.expiryDate)).length,
  }), [inductions])

  const filteredWorkers = useMemo(() => inductions.filter(w => {
    const s = search.toLowerCase()
    const matchSearch = !s ||
      w.workerName.toLowerCase().includes(s) ||
      (w.company?.toLowerCase().includes(s)) ||
      (w.uniqueWorkerId?.includes(s)) ||
      (w.inductionTitle.toLowerCase().includes(s))
    const matchStatus = statusFilter === 'ALL' || w.status === statusFilter
    const matchType   = typeFilter === 'ALL'   || w.inductionType === typeFilter
    const matchJob    = jobFilter === 'ALL'    || w.jobId === jobFilter
    return matchSearch && matchStatus && matchType && matchJob
  }), [inductions, search, statusFilter, typeFilter, jobFilter])

  const expiryAlerts = inductions.filter(w =>
    w.status === 'PASSED' && (isExpiringSoon(w.expiryDate) || isExpired(w.expiryDate))
  )

  const jobsWithModules = MOCK_INDUCTION_MODULES
    .filter(m => m.jobId)
    .map(m => MOCK_JOBS.find(j => j.id === m.jobId))
    .filter(Boolean)

  // ── Handlers ──────────────────────────────────────────────────
  const handleRegister = (partial: Partial<WorkerInduction>) => {
    setInductions(prev => [partial as WorkerInduction, ...prev])
  }

  const handleReinduct = (w: WorkerInduction) => {
    setInductions(prev => prev.map(i =>
      i.id === w.id ? { ...i, status: 'PENDING', uniqueWorkerId: undefined, score: undefined, passed: undefined, completedAt: undefined } : i
    ))
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6">

      {/* Expiry alert banner */}
      {expiryAlerts.length > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>{expiryAlerts.length} worker induction{expiryAlerts.length !== 1 ? 's' : ''}</strong> expiring or expired —{' '}
            {expiryAlerts.map(w => w.workerName).join(', ')}.
            <button onClick={() => setStatusFilter('PASSED')} className="ml-2 underline underline-offset-2 hover:text-amber-900">
              View
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Site Inductions</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Worker onboarding, credential verification, and site access control
            {liveInductionCount > 0 && <span className="ml-2 text-blue-600 font-medium">· {liveInductionCount} live from Neon</span>}
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowRegister(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <UserPlus className="w-4 h-4" /> Register worker
          </button>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Inducted',         value: stats.passed,   color: 'text-green-600',  bg: 'bg-green-50',  filter: 'PASSED'  as InductionStatus },
          { label: 'Pending',          value: stats.pending,  color: 'text-blue-600',   bg: 'bg-blue-50',   filter: 'PENDING' as InductionStatus },
          { label: 'Failed',           value: stats.failed,   color: 'text-red-600',    bg: 'bg-red-50',    filter: 'FAILED'  as InductionStatus },
          { label: 'Expiring ≤30 days',value: stats.expiring, color: 'text-amber-600',  bg: 'bg-amber-50',  filter: 'PASSED'  as InductionStatus },
          { label: 'Expired',          value: stats.expired,  color: 'text-slate-600',  bg: 'bg-slate-100', filter: 'EXPIRED' as InductionStatus },
        ].map(s => (
          <button
            key={s.label}
            onClick={() => { setStatusFilter(s.filter); setTab('workers') }}
            className={`${s.bg} rounded-xl p-4 text-left hover:opacity-80 transition-opacity`}
          >
            <p className="text-xs text-slate-500">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {[
          { id: 'workers',         label: `Workers (${inductions.length})`, icon: Users },
          { id: 'modules',         label: `Modules (${MOCK_INDUCTION_MODULES.length})`, icon: BookOpen },
          { id: 'qr',              label: 'QR codes', icon: QrCode },
          { id: 'training-matrix', label: 'Training matrix', icon: GraduationCap },
          { id: 'voc',             label: `VOC register${MOCK_VOC_RECORDS.filter(v=>v.status==='EXPIRED'||v.status==='EXPIRING').length > 0 ? ` ⚠${MOCK_VOC_RECORDS.filter(v=>v.status==='EXPIRED'||v.status==='EXPIRING').length}` : ''}`, icon: Shield },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id as any)}
            className={`flex-1 flex items-center justify-center gap-2 text-sm font-medium py-2 rounded-lg transition-colors ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t.label}</span>
            <span className="sm:hidden">{t.id === 'workers' ? 'Workers' : t.id === 'modules' ? 'Modules' : t.id === 'qr' ? 'QR' : t.id === 'training-matrix' ? 'Matrix' : 'VOC'}</span>
          </button>
        ))}
      </div>

      {/* ── TAB: WORKERS ─────────────────────────────────────── */}
      {tab === 'workers' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search worker name, company, site ID..."
                className="input pl-9 w-full text-sm"
              />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="input text-sm">
              <option value="ALL">All statuses</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="input text-sm">
              <option value="ALL">All types</option>
              {Object.entries(TYPE_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input text-sm">
              <option value="ALL">All projects</option>
              {MOCK_JOBS.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
            </select>
          </div>

          {/* Results count */}
          {(search || statusFilter !== 'ALL' || typeFilter !== 'ALL' || jobFilter !== 'ALL') && (
            <p className="text-xs text-slate-500">
              Showing {filteredWorkers.length} of {inductions.length} records
              <button onClick={() => { setSearch(''); setStatusFilter('ALL'); setTypeFilter('ALL'); setJobFilter('ALL') }}
                className="ml-2 text-indigo-600 hover:underline">Clear filters</button>
            </p>
          )}

          {/* Worker list */}
          {filteredWorkers.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <ClipboardCheck className="w-10 h-10 mx-auto mb-3 text-slate-200" />
              <p className="font-medium">No induction records found</p>
              <p className="text-sm mt-1">Adjust your filters or register a new worker</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {/* Table header */}
              <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b text-xs font-medium text-slate-500 uppercase tracking-wider">
                <div className="col-span-3">Worker</div>
                <div className="col-span-3">Induction</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Credentials</div>
                <div className="col-span-1">Site ID</div>
                <div className="col-span-1" />
              </div>

              <div className="divide-y divide-slate-100">
                {filteredWorkers.map(w => {
                  const credExpiring = isExpiringSoon(w.whiteCardExpiry) || isExpiringSoon(w.electricalLicenceExpiry)
                  const credExpired  = isExpired(w.whiteCardExpiry) || isExpired(w.electricalLicenceExpiry)

                  return (
                    <div
                      key={w.id}
                      onClick={() => setSelectedWorker(w)}
                      className="grid grid-cols-1 sm:grid-cols-12 gap-2 px-4 py-3.5 hover:bg-slate-50 cursor-pointer transition-colors items-center"
                    >
                      {/* Worker */}
                      <div className="sm:col-span-3 flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xs font-bold flex-shrink-0">
                          {w.workerName.split(' ').map(n => n[0]).join('').slice(0,2)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{w.workerName}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${WORKER_TYPE_META[w.workerType].bg} ${WORKER_TYPE_META[w.workerType].text}`}>
                              {WORKER_TYPE_META[w.workerType].label}
                            </span>
                            {w.company && <span className="text-xs text-slate-400 truncate">{w.company}</span>}
                          </div>
                        </div>
                      </div>

                      {/* Induction */}
                      <div className="sm:col-span-3 min-w-0">
                        <p className="text-xs text-slate-700 truncate">{w.inductionTitle}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${TYPE_META[w.inductionType].bg} ${TYPE_META[w.inductionType].text}`}>
                            {TYPE_META[w.inductionType].label}
                          </span>
                          {w.completedAt && (
                            <span className="text-xs text-slate-400">{formatDate(w.completedAt)}</span>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="sm:col-span-2">
                        <StatusBadge status={w.status} />
                        {w.expiryDate && w.status === 'PASSED' && (
                          <p className={`text-xs mt-1 ${isExpired(w.expiryDate) ? 'text-red-600' : isExpiringSoon(w.expiryDate) ? 'text-amber-600' : 'text-slate-400'}`}>
                            {isExpired(w.expiryDate) ? '⚠ Expired' : isExpiringSoon(w.expiryDate) ? `⚠ Exp ${daysUntil(w.expiryDate)}d` : `Exp ${formatDate(w.expiryDate)}`}
                          </p>
                        )}
                      </div>

                      {/* Credential health */}
                      <div className="sm:col-span-2">
                        {credExpired ? (
                          <span className="flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded-full w-fit">
                            <AlertTriangle className="w-3 h-3" /> Cred expired
                          </span>
                        ) : credExpiring ? (
                          <span className="flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full w-fit">
                            <Clock className="w-3 h-3" /> Expiring
                          </span>
                        ) : w.whiteCardNumber || w.electricalLicenceNumber ? (
                          <span className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full w-fit">
                            <CheckCircle className="w-3 h-3" /> Verified
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300 italic">—</span>
                        )}
                      </div>

                      {/* Site ID */}
                      <div className="sm:col-span-1">
                        {w.uniqueWorkerId ? (
                          <span className="text-xs font-mono font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded-lg">
                            {w.uniqueWorkerId}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="hidden sm:flex sm:col-span-1 justify-end">
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: MODULES ─────────────────────────────────────── */}
      {tab === 'modules' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Induction modules define the content and quiz workers must complete.
              Company inductions apply to all projects; project inductions are site-specific.
            </p>
            {canManage && (
              <button className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors ml-4 flex-shrink-0">
                <Plus className="w-3.5 h-3.5" /> New module
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {MOCK_INDUCTION_MODULES.map(mod => (
              <ModuleCard
                key={mod.id}
                mod={mod}
                allInductions={inductions}
                onPreview={() => setPreviewModule(mod)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── TAB: QR CODES ────────────────────────────────────── */}
      {tab === 'qr' && (
        <QRPanel modules={MOCK_INDUCTION_MODULES} />
      )}

      {/* ── TAB: TRAINING MATRIX (G-14) ─────────────────────── */}
      {tab === 'training-matrix' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
            <div className="font-semibold mb-1">Training Matrix — required training per role vs completed</div>
            WHS Regulation 2011 (Qld) Part 4.5 — all workers must hold required licences and competencies for their role before performing licensed or high-risk work. This matrix shows compliance gaps per worker.
          </div>

          {/* Role selector */}
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs font-medium text-gray-600">Filter by role:</span>
            {(['ALL', ...Object.keys(ROLE_TRAINING_MAP)] as const).map(r => (
              <button key={r} onClick={() => setRoleFilter(r)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${roleFilter===r?'bg-[#1F3864] text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{r === 'ALL' ? 'All roles' : r}</button>
            ))}
          </div>

          {/* Matrix grid */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 border-b sticky left-0 bg-gray-50 min-w-[140px]">Worker / Role</th>
                  {TRAINING_CATEGORIES.map(cat => (
                    <th key={cat} className="px-3 py-2 font-medium text-gray-500 border-b text-center min-w-[80px]" style={{writingMode:'vertical-rl',textOrientation:'mixed',height:100,paddingBottom:8}}>{cat}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(['Tom Richards','Sam Nguyen','Sarah Lee','Jane Smith'] as const).map((name, rowIdx) => {
                  const workerVOCs = MOCK_VOC_RECORDS.filter(v => v.workerName === name)
                  const completedTypes = workerVOCs.map(v => v.licenceType)
                  const workerRoles = workerVOCs.map(v => v.role)
                  const primaryRole = workerRoles[0] ?? 'Worker'
                  const required = ROLE_TRAINING_MAP[primaryRole] ?? []

                  if (roleFilter !== 'ALL' && primaryRole !== roleFilter) return null
                  return (
                    <tr key={name} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>
                      <td className="px-4 py-3 sticky left-0 bg-inherit border-b">
                        <div className="font-medium text-gray-900">{name}</div>
                        <div className="text-gray-500 text-[10px] mt-0.5">{primaryRole}</div>
                      </td>
                      {TRAINING_CATEGORIES.map(cat => {
                        const isRequired = required.includes(cat)
                        const voc = workerVOCs.find(v => v.licenceType === cat)
                        const isCompleted = !!voc
                        const status = voc?.status

                        if (!isRequired && !isCompleted) return (
                          <td key={cat} className="border-b px-2 py-3 text-center text-gray-200">—</td>
                        )
                        if (isRequired && !isCompleted) return (
                          <td key={cat} className="border-b px-2 py-3 text-center"><span className="text-red-500 font-bold text-sm" title="Required — not completed">✗</span></td>
                        )
                        if (status === 'EXPIRED') return (
                          <td key={cat} className="border-b px-2 py-3 text-center bg-red-50"><span className="text-red-600 font-bold text-sm" title="Expired">⊗</span></td>
                        )
                        if (status === 'EXPIRING') return (
                          <td key={cat} className="border-b px-2 py-3 text-center bg-amber-50"><span className="text-amber-600 font-bold text-sm" title="Expiring soon">⚠</span></td>
                        )
                        return (
                          <td key={cat} className="border-b px-2 py-3 text-center"><span className="text-green-600 font-bold text-sm" title="Current">✓</span></td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="flex gap-4 flex-wrap text-xs text-gray-600">
            {[{s:'✓',c:'text-green-600',l:'Completed & current'},{s:'⚠',c:'text-amber-600',l:'Expiring within 60 days'},{s:'⊗',c:'text-red-600',l:'Expired — must not perform this work'},{s:'✗',c:'text-red-500',l:'Required but not completed'},{s:'—',c:'text-gray-300',l:'Not required for this role'}].map(({s,c,l}) => (
              <div key={l} className="flex items-center gap-1.5"><span className={`font-bold ${c}`}>{s}</span><span>{l}</span></div>
            ))}
          </div>

          <div className="flex justify-end"><button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export training status report</button></div>
        </div>
      )}

      {/* ── TAB: VOC REGISTER (G-14) ─────────────────────────── */}
      {tab === 'voc' && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 flex-1">
              <div className="font-semibold mb-1">Verification of Competency (VOC) Register — WHS Reg 2011 (Qld) Part 4.5</div>
              All trade licences, certificates, and plant-specific VOCs must be verified and recorded before the worker performs licensed or high-risk work. Expiry alerts are sent to the WHSO and the worker (via MobileComms) 60 days before expiry.
            </div>
            <button className="btn-secondary text-sm flex items-center gap-2 flex-shrink-0"><Plus className="w-4 h-4"/>Add VOC record</button>
          </div>

          {MOCK_VOC_RECORDS.filter(v => v.status === 'EXPIRED').length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/>
              <span><strong>{MOCK_VOC_RECORDS.filter(v=>v.status==='EXPIRED').length} expired licence{MOCK_VOC_RECORDS.filter(v=>v.status==='EXPIRED').length!==1?'s':''}</strong> — workers must not perform work requiring this licence until renewed.</span>
            </div>
          )}

          {/* Filter */}
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"/><input className="input pl-9 text-sm" placeholder="Search worker or licence…" value={vocSearch} onChange={e => setVocSearch(e.target.value)}/></div>
            {(['ALL','CURRENT','EXPIRING','EXPIRED','PERMANENT'] as (VOCStatus|'ALL')[]).map(f => {
              const cnt = f==='ALL' ? MOCK_VOC_RECORDS.length : MOCK_VOC_RECORDS.filter(v=>v.status===f).length
              if (cnt===0 && f!=='ALL') return null
              return <button key={f} onClick={() => setVocFilter(f)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${vocFilter===f?'bg-[#1F3864] text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f==='ALL'?`All (${cnt})`:(`${(VOC_STATUS_META as any)[f]?.label} (${cnt})`)}</button>
            })}
          </div>

          {/* VOC table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {['Worker','Role','Licence / VOC type','Licence number','Issuing body','Issue date','Expiry','Status','Verified by'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MOCK_VOC_RECORDS
                  .filter(v => (vocFilter==='ALL'||v.status===vocFilter) && (!vocSearch || v.workerName.toLowerCase().includes(vocSearch.toLowerCase()) || v.licenceType.toLowerCase().includes(vocSearch.toLowerCase())))
                  .map(v => {
                    const sm = (VOC_STATUS_META as any)[v.status] ?? VOC_STATUS_META.CURRENT
                    const daysToExpiry = v.expiryDate ? Math.ceil((new Date(v.expiryDate).getTime() - Date.now()) / 86400000) : null
                    return (
                      <tr key={v.id} className={`border-b last:border-0 ${v.status==='EXPIRED'?'bg-red-50/40':v.status==='EXPIRING'?'bg-amber-50/30':''}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{v.workerName}</td>
                        <td className="px-4 py-3 text-xs text-gray-600">{v.role}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">{v.licenceType}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{v.licenceNumber ?? '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{v.issuingBody}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{v.issueDate}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                          {v.expiryDate ? (
                            <span className={`font-medium ${v.status==='EXPIRED'?'text-red-700':v.status==='EXPIRING'?'text-amber-700':'text-gray-700'}`}>
                              {v.expiryDate}
                              {daysToExpiry !== null && daysToExpiry > 0 && <div className="text-[10px] text-amber-600">{daysToExpiry}d remaining</div>}
                              {daysToExpiry !== null && daysToExpiry <= 0 && <div className="text-[10px] text-red-600">EXPIRED {Math.abs(daysToExpiry)}d ago</div>}
                            </span>
                          ) : <span className="text-gray-400">No expiry</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${sm.bg} ${sm.text}`}>{sm.label}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{v.verifiedByName}</td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end"><button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export VOC register</button></div>
        </div>
      )}

      {/* ── MODALS ────────────────────────────────────────────── */}
      {selectedWorker && (
        <WorkerDetailModal
          worker={selectedWorker}
          allWorkers={inductions}
          onClose={() => setSelectedWorker(null)}
          onReinduct={handleReinduct}
        />
      )}

      {previewModule && (
        <QuizSimulatorModal
          module={previewModule}
          onClose={() => setPreviewModule(null)}
        />
      )}

      {showRegister && (
        <RegisterWorkerModal
          onClose={() => setShowRegister(false)}
          onRegister={handleRegister}
        />
      )}
    </div>
  )
}
